import loadable from "@loadable/component";
import { FC, RefObject, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import Chevron from "@Icons/Chevron";
import { webAppBackHandler } from "@Utils/webApputils";
import SearchModalDesktop from "@Components/SearchModalDesktop";
import config from "@Config/index";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useLanguagePath } from "hooks/useLanguagePath";

import useSearchKeywords from "../../searchKeywords";
import useCopy from "../../useCopy";
import heroStyles from "../Hero/HomeV2Hero.module.scss";
import HeroNav from "../HeroNav";
import HomeV2SearchAiIcon from "../SearchAiIcon";
import styles from "./HomeV2ScrolledHeader.module.scss";

// The phone's full-page search (the live one), fetched on the first tap.
const MobileSearch = loadable(() => import("../MobileSearch"));

/**
 * The header's SCROLLED state (Airbnb's shape): once the hero has scrolled away, a
 * white bar slides down with the dark amber logo, a compact search pill in the middle
 * and the hero's own controls on the right (HeroNav, tone="light").
 *
 * The search pill opens IN PLACE, as Airbnb's does: a click grows it into the full
 * search — the live `SearchModalDesktop`, the same component (and the same hero skin
 * and animated AI search icon) as the hero's — with its suggestion dropdown hanging
 * under the bar, and the page dimmed behind. Escape, a click outside or a scroll
 * closes it back to the pill.
 */

/** The hero's typing rhythm, so the two bars read as one search. */
const TYPE_MS = 55;
const DELETE_MS = 28;
const HOLD_MS = 2000;
const PAUSE_MS = 400;

/** One pass of the animated search icon. */
const ICON_PASS_MS = 1600;

/** Past this many px (the hero is 480px), the bar is down. */
const SHOW_AFTER = 420;

type ScrolledHeaderProps = {
  /** Non-home pages: show the white bar immediately (no hero scroll threshold). */
  alwaysVisible?: boolean;
  hideSearchBar?: boolean;
  hidePartnerMenuTiles?: boolean;
  scrollPastRef?: RefObject<HTMLElement | null>;
  /**
   * Referral-style hero: one fixed bar for the whole page. Before `scrollPastRef` crosses
   * the top: logo + nav. After: search + nav (no logo on phone). Replaces a second hero header.
   */
  heroScrollChrome?: boolean;
  showBackInsteadOfLogo?: boolean;
  /** Back chevron plus home logo (book/payment forms). */
  showBackWithLogo?: boolean;
  /** Shown beside back when `showBackInsteadOfLogo` (profile sub-routes, settings). */
  barTitle?: string;
  inFlow?: boolean;
  /**
   * Site header (SiteHeaderBar) on every route but home: the phone bar is one 60px row.
   * The new home page renders this bar itself and keeps its own phone layout.
   */
  phoneRow?: boolean;
};

// .cn shows its own mark (安泊 | amber), as the old China header did; it is wider.
const LOGO = config.IS_CHINA_APP
  ? `${config.IMAGE_STATIC_URL}/images/logo/amber-china-logo-dark.svg?auto=format&q=40`
  : `${config.IMAGE_STATIC_ASSETS_COMPONENTS_PATH}/Header/assets/amber-logo-dark.svg`;
const LOGO_ALT = config.IS_CHINA_APP ? "安泊 | amber" : "amber";
const LOGO_WIDTH = config.IS_CHINA_APP ? "120" : "100";

const ScrolledHeader: FC<ScrolledHeaderProps> = ({
  alwaysVisible = false,
  hideSearchBar = false,
  hidePartnerMenuTiles = false,
  scrollPastRef,
  heroScrollChrome = false,
  showBackInsteadOfLogo = false,
  showBackWithLogo = false,
  barTitle = "",
  inFlow = false,
  phoneRow = false,
}) => {
  const history = useHistory();
  const { getLocalizedPath } = useLanguagePath();
  const [shown, setShown] = useState(alwaysVisible);
  const [pastHero, setPastHero] = useState(false);
  const [open, setOpen] = useState(false);
  // Phone: the pill opens the live full-page search, not the in-place one.
  const isDesktop = useSelector(({ appData }: AppState) => appData.isDesktop);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  /** Whether the open search's input is empty — the hint shows only then. */
  const [empty, setEmpty] = useState(true);
  const searchRef = useRef<HTMLDivElement>(null);
  const [iconPlaying, setIconPlaying] = useState(false);
  const iconTimer = useRef(0);
  const playIcon = () => {
    if (iconTimer.current) return;
    setIconPlaying(true);
    iconTimer.current = window.setTimeout(() => {
      iconTimer.current = 0;
      setIconPlaying(false);
    }, ICON_PASS_MS);
  };
  useEffect(() => () => window.clearTimeout(iconTimer.current), []);

  // The bar says what the hero says: "Search by" and the same words typing in and out —
  // in the collapsed pill while the header is showing, and as the open search's
  // placeholder until something is typed.
  const searchKeywords = useSearchKeywords();
  const [promptIndex, setPromptIndex] = useState(0);
  const [typed, setTyped] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const copy = useCopy();
  const word = searchKeywords[promptIndex];
  const barVisible = heroScrollChrome || shown;
  const showSearchInBar = !hideSearchBar;
  const showLogoInBar =
    showBackInsteadOfLogo || showBackWithLogo || !heroScrollChrome || !pastHero || isDesktop;

  useEffect(() => {
    if (open ? !empty : !barVisible || !showSearchInBar) return undefined;
    const full = word;
    let id: number;
    // `>=`: the word can get shorter mid-type when its translation arrives.
    if (!deleting && typed >= full.length) {
      id = window.setTimeout(() => setDeleting(true), HOLD_MS);
    } else if (deleting && typed === 0) {
      id = window.setTimeout(() => {
        setDeleting(false);
        setPromptIndex((i) => (i + 1) % searchKeywords.length);
      }, PAUSE_MS);
    } else {
      id = window.setTimeout(
        () => setTyped((n) => n + (deleting ? -1 : 1)),
        deleting ? DELETE_MS : TYPE_MS,
      );
    }
    return () => window.clearTimeout(id);
  }, [open, empty, barVisible, showSearchInBar, promptIndex, typed, deleting, word]);

  useEffect(() => {
    if (heroScrollChrome && scrollPastRef) {
      let frame = 0;
      const onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          const el = scrollPastRef.current;
          setPastHero(Boolean(el && el.getBoundingClientRect().top < 0));
        });
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", onScroll);
        if (frame) window.cancelAnimationFrame(frame);
      };
    }
    if (alwaysVisible) {
      setShown(true);
      return undefined;
    }
    if (scrollPastRef) {
      let frame = 0;
      const onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          const el = scrollPastRef.current;
          setShown(Boolean(el && el.getBoundingClientRect().top < 0));
        });
      };
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", onScroll);
        if (frame) window.cancelAnimationFrame(frame);
      };
    }
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setShown(window.scrollY > SHOW_AFTER);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [alwaysVisible, scrollPastRef, heroScrollChrome]);

  // Open: focus the real input once it has mounted, which opens its dropdown.
  useEffect(() => {
    if (!open) return undefined;
    setEmpty(true);
    setTyped(0);
    setDeleting(false);
    playIcon();
    const id = window.setTimeout(
      () => searchRef.current?.querySelector<HTMLInputElement>("input")?.focus(),
      30,
    );
    const onDown = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const startY = window.scrollY;
    const onScroll = () => Math.abs(window.scrollY - startY) > 80 && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open]);

  // The bar going away (scrolled back to the hero) closes the search with it.
  useEffect(() => {
    if (!barVisible) setOpen(false);
  }, [barVisible]);

  const searchSection = (() => {
    if (!showSearchInBar) return null;
    if (open) {
      return (
        <div
          className={`${heroStyles.search} ${styles.searchOpen}`}
          ref={searchRef}
          onMouseEnter={playIcon}
          onInput={(e) => setEmpty(!(e.target as HTMLInputElement).value)}
        >
          <SearchModalDesktop searchBarCategory={PAGE.PROPERTY} />
          {/* The hero's typing words as the placeholder, until you start typing. */}
          {empty && (
            <span className={heroStyles.prompt} aria-hidden="true">
              <span className={heroStyles.promptStatic}>
                {copy("searchpage.components.header.searchtitle", "Search by")}&nbsp;
              </span>
              <span className={heroStyles.promptWords}>
                <span className={heroStyles.promptWord}>{word.slice(0, typed)}</span>
                <span
                  className={`${heroStyles.caret} ${
                    typed >= word.length && !deleting ? heroStyles.caretIdle : ""
                  }`}
                />
              </span>
            </span>
          )}
          <span className={heroStyles.searchIconArt}>
            <HomeV2SearchAiIcon playing={iconPlaying} />
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`${styles.search} ${isDesktop ? "" : styles.searchPhone}`.trim()}
        onClick={() => {
          if (isDesktop) {
            setOpen(true);
            return;
          }
          // Phone: the live sticky search's funnel event (desktop's search modal
          // sends its own).
          gtagEventEmitter({ action: "search_button_clicked", category: PAGE.SEARCH });
          setMobileSearchOpen(true);
        }}
        onMouseEnter={playIcon}
        tabIndex={barVisible ? 0 : -1}
        aria-label={copy("home.v3.search.scrolled", "Search a city, university or property")}
      >
        <span className={styles.searchText} aria-hidden="true">
          <span className={heroStyles.promptStatic}>
            {copy("searchpage.components.header.searchtitle", "Search by")}&nbsp;
          </span>
          <span className={heroStyles.promptWords}>
            <span className={heroStyles.promptWord}>{word.slice(0, typed)}</span>
            <span
              className={`${heroStyles.caret} ${
                typed >= word.length && !deleting ? heroStyles.caretIdle : ""
              }`}
            />
          </span>
        </span>
        <span className={styles.searchButton} aria-hidden="true">
          <HomeV2SearchAiIcon playing={iconPlaying} />
        </span>
      </button>
    );
  })();

  const compactBar = heroScrollChrome && pastHero && !isDesktop;

  const backButton = (
    <button
      type="button"
      className={styles.back}
      onClick={() => webAppBackHandler(history)}
      aria-label="Back"
      tabIndex={barVisible ? 0 : -1}
    >
      <Chevron rotate="90" height="16" width="16" />
    </button>
  );

  const logoLink = (
    <CustomLink
      to={getLocalizedPath("/")}
      className={styles.logo}
      tabIndex={barVisible ? 0 : -1}
      onClick={() => gtagEventEmitter({ action: "clicked_amber", category: "" })}
    >
      <Image src={LOGO} alt={LOGO_ALT} width={LOGO_WIDTH} height="24" isNotLazy />
    </CustomLink>
  );

  let barLead: JSX.Element | null = null;
  if (showBackWithLogo) {
    barLead = (
      <div className={styles.leadCluster}>
        {backButton}
        {logoLink}
      </div>
    );
  } else if (showBackInsteadOfLogo) {
    barLead = (
      <div className={styles.leadCluster}>
        {backButton}
        {barTitle ? <h1 className={styles.barTitle}>{barTitle}</h1> : null}
      </div>
    );
  } else {
    barLead = logoLink;
  }

  return (
    <>
      {/* The page dims behind the open search, as on Airbnb. */}
      <div className={`${styles.scrim} ${open ? styles.scrimOn : ""}`} aria-hidden="true" />
      <header
        className={`${styles.bar} ${inFlow ? styles.barInFlow : ""} ${
          barVisible ? styles.shown : ""
        } ${open ? styles.barOpen : ""} ${open ? styles.barSearchOpen : ""} ${
          heroScrollChrome ? styles.barPinned : ""
        } ${phoneRow && !isDesktop ? styles.phone : ""}`}
        aria-hidden={!barVisible}
        data-testid="v2-scrolled-header"
      >
        <div
          className={`${styles.inner} ${!showSearchInBar ? styles.innerNoSearch : ""} ${
            compactBar ? styles.innerCompact : ""
          }`.trim()}
        >
          {showLogoInBar && barLead}

          {searchSection}

          <div className={styles.right}>
            <HeroNav tone="light" hidePartnerMenuTiles={hidePartnerMenuTiles} />
          </div>
        </div>
      </header>
      {mobileSearchOpen && <MobileSearch onClose={() => setMobileSearchOpen(false)} />}
    </>
  );
};

export default wrapperHOC(ScrolledHeader, {
  componentName: "HomeV2-ScrolledHeader",
  showForChina: true,
  showForWhiteLable: false,
});
